import { ChangeEvent, useCallback, useEffect, useState } from 'react';
import { Box, Icon, Link } from '@nimbus-ds/components';
import { ChevronLeftIcon, CheckCircleIcon } from '@nimbus-ds/icons';
import { SideModal, EmptyMessage } from '@nimbus-ds/patterns';
import { CancelAndConfirmButtons, Stack } from 'commons/components';
import { SelectedProductInterface } from 'domains/Catalog/Products/components/SelectProductList/types';
import useTranslationShipping from 'domains/Shipping/useTranslationShipping';
import ProductsList from './ProductsList';
import GenericSelectionModalHeader from '../shared/GenericSelectionModal/GenericSelectionModalHeader';
import ModalFooter from '../shared/ModalFooter';

interface ProductsSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (variantIds: string[]) => void;
  preselectedVariantIds: string[];
  products: SelectedProductInterface[];
  fetchProducts: (term?: string) => Promise<void>;
  loadMoreProducts: (term?: string) => Promise<void>;
  isLoading: boolean;
  isLoadingMore: boolean;
  hasMoreProducts: boolean;
}

function ProductsSelectionModal({
  isOpen,
  onClose,
  onSubmit,
  preselectedVariantIds,
  products,
  fetchProducts,
  loadMoreProducts,
  isLoading,
  isLoadingMore,
  hasMoreProducts,
}: Readonly<ProductsSelectionModalProps>): JSX.Element {
  const t = useTranslationShipping(
    'deliveryMethods.shippingLimiting.modals.products',
  );
  const tCommon = useTranslationShipping(
    'deliveryMethods.shippingLimiting.modals.common',
  );

  const [selectedVariantIds, setSelectedVariantIds] = useState<string[]>(
    preselectedVariantIds,
  );
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    if (!isOpen) return undefined;

    const debouncedFetch = setTimeout(
      () => {
        fetchProducts(searchTerm);
      },
      searchTerm ? 300 : 0,
    );

    return () => clearTimeout(debouncedFetch);
  }, [searchTerm, isOpen, fetchProducts]);

  const handleSearchChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  }, []);

  const handleAddVariants = useCallback((variantIds: string[]) => {
    setSelectedVariantIds((prev) => [...new Set([...prev, ...variantIds])]);
  }, []);

  const handleRemoveVariants = useCallback((variantIds: string[]) => {
    setSelectedVariantIds((prev) =>
      prev.filter((id) => !variantIds.includes(id)),
    );
  }, []);

  const handleSubmit = useCallback(() => {
    onSubmit(selectedVariantIds);
  }, [selectedVariantIds, onSubmit]);

  const handleCancel = useCallback(() => {
    setSelectedVariantIds(preselectedVariantIds);
    setSearchTerm('');
    onClose();
  }, [preselectedVariantIds, onClose]);

  const handleLoadMore = useCallback(() => {
    loadMoreProducts(searchTerm);
  }, [loadMoreProducts, searchTerm]);

  return (
    <SideModal
      open={isOpen}
      onRemove={onClose}
      maxWidth={{ xs: '100%', md: '45%', lg: '540px' }}
      paddingBody="none"
      headerAction={
        <Link onClick={handleCancel} textDecoration="none">
          <Icon color="primary-textHigh" source={<ChevronLeftIcon />} />
        </Link>
      }
      headerIcon={
        <Link onClick={handleSubmit} textDecoration="none">
          <Icon source={<CheckCircleIcon />} />
        </Link>
      }
    >
      <Stack column align="stretch" height="calc(100vh - 85px)">
        <GenericSelectionModalHeader
          title={t('title')}
          description={t('description')}
          totalItems={products.length}
          selectedCount={selectedVariantIds.length}
          searchTerm={searchTerm}
          searchPlaceholder={t('searchPlaceholder')}
          counterText={t('counter', { count: selectedVariantIds.length })}
          minItemsForSearch={0}
          onSearchChange={handleSearchChange}
          onClearSelection={() => setSelectedVariantIds([])}
        />

        {!isLoading && products.length === 0 && searchTerm ? (
          <Box flex="1" overflow="auto" overflowX="hidden">
            <EmptyMessage
              title={tCommon('emptySearchTitle')}
              text={tCommon('emptySearchMessage')}
            />
          </Box>
        ) : (
          <ProductsList
            products={products}
            selectedVariantIds={selectedVariantIds}
            onAdd={handleAddVariants}
            onRemove={handleRemoveVariants}
            onLoadMore={handleLoadMore}
            isLoading={isLoading}
            isLoadingMore={isLoadingMore}
            hasMoreProducts={hasMoreProducts}
          />
        )}

        <ModalFooter>
          <CancelAndConfirmButtons
            onCancel={handleCancel}
            onConfirm={handleSubmit}
            cancelText={tCommon('cancel')}
            confirmText={tCommon('save')}
          />
        </ModalFooter>
      </Stack>
    </SideModal>
  );
}

export default ProductsSelectionModal;
